import { createContext, useContext, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EnquireContext = createContext(null);
export const useEnquire = () => useContext(EnquireContext);

const inputCls =
  "w-full border-b border-white/20 bg-transparent py-3 text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-[#4A7BA6]";

const ContactModal = ({ open, onClose }) => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", interest: t.form.interests[0], message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success(t.form.success);
      setForm({ name: "", email: "", interest: t.form.interests[0], message: "" });
      onClose();
    } catch (err) {
      if (err?.response?.status === 422) {
        toast.error(t.form.validation);
      } else {
        toast.error(t.form.error);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" data-testid="contact-modal">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, y: 64 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 64 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-lg border border-white/10 bg-[#10161E] p-8 sm:p-12"
          >
            <button
              data-testid="contact-close-button"
              onClick={onClose}
              aria-label="Close"
              className="absolute right-6 top-6 text-white/50 transition-colors hover:text-[#4A7BA6]"
            >
              <X size={22} />
            </button>
            <p className="font-tech text-xs uppercase tracking-[0.4em] text-[#4A7BA6]">Jano Automobili</p>
            <h3 className="mt-3 font-heritage text-3xl italic">{t.form.title}</h3>
            <form onSubmit={submit} className="mt-10 flex flex-col gap-8">
              <input
                data-testid="contact-name-input"
                required
                minLength={2}
                value={form.name}
                onChange={set("name")}
                placeholder={t.form.name}
                className={inputCls}
              />
              <input
                data-testid="contact-email-input"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder={t.form.email}
                className={inputCls}
              />
              <select
                data-testid="contact-interest-select"
                value={form.interest}
                onChange={set("interest")}
                className={`${inputCls} cursor-pointer appearance-none bg-[#10161E]`}
              >
                {t.form.interests.map((i) => (
                  <option key={i} value={i} className="bg-[#10161E]">{i}</option>
                ))}
              </select>
              <textarea
                data-testid="contact-message-input"
                required
                minLength={10}
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder={t.form.message}
                className={`${inputCls} resize-none`}
              />
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                className="rounded-full bg-[#4A7BA6] py-4 font-tech text-sm uppercase tracking-[0.3em] text-white transition-colors duration-300 hover:bg-white hover:text-black disabled:opacity-50"
              >
                {sending ? t.form.sending : t.form.submit}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export const EnquireProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  return (
    <EnquireContext.Provider value={{ open, setOpen }}>
      {children}
      <ContactModal open={open} onClose={() => setOpen(false)} />
    </EnquireContext.Provider>
  );
};
